import type { ComponentType } from "react";

export const BASE_URL = "https://rugira.ch";
export const AVAILABLE_LANGUAGES = ["en", "de", "fr", "it"] as const;

export type LanguageCode = (typeof AVAILABLE_LANGUAGES)[number];

type RouteModule = { default: ComponentType<any> };
type LocalizedContent = string | Partial<Record<LanguageCode, string>>;

export type RouteDefinition = {
  path: string;
  loader: () => Promise<RouteModule>;
  indexable: boolean;
  renderGlobalNavigation: boolean;
  seo: {
    title: LocalizedContent;
    description: LocalizedContent;
    ogTitle?: LocalizedContent;
    ogDescription?: LocalizedContent;
    ogImage?: string;
    noIndex?: boolean;
  };
};

const embeddedNavigationPaths = new Set([
  "/partners",
  "/roadmap",
  "/status",
  "/use-cases",
  "/investors",
  "/press",
  "/compare",
  "/security",
  "/samples",
  "/roi",
]);

const localize = (value: string): Partial<Record<LanguageCode, string>> => ({
  en: value,
});

export const ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    path: "/",
    loader: () => import("@/pages/home"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: {
        en: "Rugira - Controlled Agentic Crypto Trading for Professional Desks",
        de: "Rugira - Kontrolliertes agentisches Krypto-Trading für professionelle Desks",
        fr: "Rugira - Trading crypto agentique contrôlé pour desks professionnels",
        it: "Rugira - Trading crypto agentico controllato per desk professionali",
      },
      description: {
        en: "Request early access to Rugira's controlled agentic crypto trading preview for professional desks. Policy-gated execution, approvals, multi-venue routing, and audit-ready evidence.",
        de: "Frühen Zugang zur Rugira-Vorschau für kontrolliertes agentisches Krypto-Trading anfragen. Richtliniengesteuerte Ausführung, Freigaben, Multi-Venue-Routing und auditfähige Nachweise.",
        fr: "Demandez un accès anticipé à l’aperçu Rugira pour trading crypto agentique contrôlé destiné aux desks professionnels. Exécution contrôlée par politiques, approbations, routage multi-venues et preuves prêtes pour l’audit.",
        it: "Richiedi accesso anticipato alla preview Rugira per trading crypto agentico controllato destinata a desk professionali. Esecuzione governata da policy, approvazioni, routing multi-venue ed evidenze pronte per l’audit.",
      },
      ogTitle: {
        en: "Controlled Agentic Crypto Trading for Professional Desks",
        de: "Kontrolliertes agentisches Krypto-Trading für professionelle Desks",
        fr: "Trading crypto agentique contrôlé pour desks professionnels",
        it: "Trading crypto agentico controllato per desk professionali",
      },
      ogDescription: {
        en: "Rugira helps professional crypto desks request access to supervised AI-assisted trading workflows with mandates, risk limits, approvals, and audit evidence before execution reaches an exchange.",
        de: "Rugira hilft professionellen Krypto-Desks, Zugang zu überwachten KI-gestützten Trading-Workflows mit Mandaten, Risikolimits, Freigaben und Audit-Evidenz anzufragen.",
        fr: "Rugira aide les desks crypto professionnels à demander l’accès à des workflows de trading assistés par IA, avec mandats, limites de risque, approbations et preuves d’audit.",
        it: "Rugira aiuta i desk crypto professionali a richiedere accesso a workflow di trading assistiti dall’IA con mandati, limiti di rischio, approvazioni ed evidenze di audit.",
      },
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/one-pager",
    loader: () => import("@/pages/one-pager"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Rugira One-Pager | Controlled Agentic Crypto Trading"),
      description: localize("A concise overview of Rugira's controlled agentic crypto trading posture, policy gates, and audit-ready execution workflows."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/login",
    loader: () => import("@/pages/login"),
    indexable: false,
    renderGlobalNavigation: false,
    seo: {
      title: localize("Login | Rugira"),
      description: localize("Secure login for Rugira users."),
      ogImage: "/brand/logo-wordmark-black.png",
      noIndex: true,
    },
  },
  {
    path: "/register",
    loader: () => import("@/pages/register"),
    indexable: false,
    renderGlobalNavigation: false,
    seo: {
      title: localize("Register | Rugira"),
      description: localize("Create a Rugira account."),
      ogImage: "/brand/logo-wordmark-black.png",
      noIndex: true,
    },
  },
  {
    path: "/dashboard",
    loader: () => import("@/pages/dashboard"),
    indexable: false,
    renderGlobalNavigation: false,
    seo: {
      title: localize("Dashboard | Rugira"),
      description: localize("Rugira trading dashboard."),
      ogImage: "/brand/logo-wordmark-black.png",
      noIndex: true,
    },
  },
  {
    path: "/security",
    loader: () => import("@/pages/security"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/security"),
    seo: {
      title: localize("Security and Control | Rugira"),
      description: localize("Swiss-grade security, approval controls, and institutional safeguards for supervised agentic crypto execution."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/samples",
    loader: () => import("@/pages/samples"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/samples"),
    seo: {
      title: localize("Sample Reports | Rugira"),
      description: localize("Download sample reports and audit extracts showing Rugira's controlled execution evidence."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/roi",
    loader: () => import("@/pages/roi"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/roi"),
    seo: {
      title: localize("ROI Calculator | Rugira"),
      description: localize("Estimate the return impact of disciplined execution and risk controls with Rugira's ROI calculator."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/partners",
    loader: () => import("@/pages/partners"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/partners"),
    seo: {
      title: localize("Partners | Rugira"),
      description: localize("Explore design partner opportunities for Rugira's controlled agentic crypto execution platform."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/roadmap",
    loader: () => import("@/pages/roadmap"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/roadmap"),
    seo: {
      title: localize("Product Roadmap | Rugira"),
      description: localize("See the roadmap for Rugira's supervised agent workflows, policy gates, and execution controls."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/changelog",
    loader: () => import("@/pages/changelog"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Changelog | Rugira"),
      description: localize("Track recent product updates across Rugira's controlled agentic trading infrastructure."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/status",
    loader: () => import("@/pages/status"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/status"),
    seo: {
      title: localize("System Status | Rugira"),
      description: localize("Monitor service health and availability across Rugira's trading infrastructure."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/use-cases",
    loader: () => import("@/pages/use-cases"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/use-cases"),
    seo: {
      title: localize("Use Cases | Rugira"),
      description: localize("See how family offices, funds, and brokers use Rugira for AI-driven execution and risk-managed trading."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/investors",
    loader: () => import("@/pages/investors"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/investors"),
    seo: {
      title: localize("Investor Overview | Rugira"),
      description: localize("Investor overview for Rugira's controlled agentic crypto trading and execution control layer."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/press",
    loader: () => import("@/pages/press"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/press"),
    seo: {
      title: localize("Press Kit | Rugira"),
      description: localize("Media assets, company information, and brand resources for Rugira."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/compare",
    loader: () => import("@/pages/compare"),
    indexable: true,
    renderGlobalNavigation: !embeddedNavigationPaths.has("/compare"),
    seo: {
      title: localize("Solution Comparison | Rugira"),
      description: localize("Compare Rugira's controlled agentic trading layer with alternative institutional execution approaches."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/privacy",
    loader: () => import("@/pages/privacy"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Privacy Policy | Rugira"),
      description: localize("Read Rugira's privacy policy and data handling commitments."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/terms",
    loader: () => import("@/pages/terms"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Terms of Service | Rugira"),
      description: localize("Read Rugira's terms of service."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/compliance",
    loader: () => import("@/pages/compliance"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Compliance | Rugira"),
      description: localize("Compliance capabilities for Rugira's supervised agentic trading workflows and execution controls."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/gdpr",
    loader: () => import("@/pages/gdpr"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("GDPR & FADP | Rugira"),
      description: localize("Rugira's GDPR and Swiss FADP approach for controlled agentic trading infrastructure."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/contact",
    loader: () => import("@/pages/contact"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Contact Rugira"),
      description: localize("Contact Rugira to discuss controlled agentic crypto execution, policy gates, and audit evidence."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/about",
    loader: () => import("@/pages/about"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("About Rugira"),
      description: localize("Learn about Rugira and the team building the control layer for agentic crypto trading."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/features",
    loader: () => import("@/pages/features"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Features | Rugira"),
      description: localize("Explore Rugira's agent mandates, policy-gated execution, approvals, decision trails, and reporting features."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/api",
    loader: () => import("@/pages/api"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("API | Rugira"),
      description: localize("API capabilities for integrating Rugira's controlled execution and reporting infrastructure."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/blog",
    loader: () => import("@/pages/blog"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Blog | Rugira"),
      description: localize("Insights on controlled agentic trading, execution governance, and Swiss crypto operations from Rugira."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
  {
    path: "/careers",
    loader: () => import("@/pages/careers"),
    indexable: true,
    renderGlobalNavigation: true,
    seo: {
      title: localize("Careers | Rugira"),
      description: localize("Career opportunities at Rugira."),
      ogImage: "/brand/logo-wordmark-black.png",
    },
  },
];

export const NOT_FOUND_ROUTE = {
  loader: () => import("@/pages/not-found"),
  seo: {
    title: "Page Not Found | Rugira",
    description: "The page you requested could not be found.",
    noIndex: true,
    ogImage: "/brand/logo-wordmark-black.png",
  },
};

export function normalizePathname(pathname: string) {
  const [withoutQuery] = pathname.split(/[?#]/, 1);
  const normalized = withoutQuery || "/";
  if (normalized.length > 1 && normalized.endsWith("/")) {
    return normalized.slice(0, -1);
  }
  return normalized;
}

export function getLanguageFromPath(pathname: string): LanguageCode {
  const path = normalizePathname(pathname);
  const segments = path.split("/").filter(Boolean);
  const maybeLanguage = segments[0] as LanguageCode | undefined;

  if (maybeLanguage && AVAILABLE_LANGUAGES.includes(maybeLanguage)) {
    return maybeLanguage;
  }

  return "en";
}

export function stripLanguageFromPath(pathname: string) {
  const path = normalizePathname(pathname);
  const segments = path.split("/").filter(Boolean);
  const maybeLanguage = segments[0] as LanguageCode | undefined;

  if (maybeLanguage && AVAILABLE_LANGUAGES.includes(maybeLanguage)) {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }

  return path;
}

export function localizePath(basePath: string, language: LanguageCode) {
  return language === "en" ? basePath : `/${language}${basePath === "/" ? "" : basePath}`;
}

export function getRouteDefinition(pathname: string) {
  const basePath = stripLanguageFromPath(pathname);
  return ROUTE_DEFINITIONS.find((route) => route.path === basePath);
}

export function resolveLocalizedValue(value: LocalizedContent, language: LanguageCode) {
  if (typeof value === "string") {
    return value;
  }

  return value[language] || value.en || Object.values(value)[0] || "";
}

export function getRouteSeoData(pathname: string) {
  const language = getLanguageFromPath(pathname);
  const route = getRouteDefinition(pathname);

  if (!route) {
    const canonicalPath = normalizePathname(pathname);
    return {
      language,
      title: NOT_FOUND_ROUTE.seo.title,
      description: NOT_FOUND_ROUTE.seo.description,
      ogImage: NOT_FOUND_ROUTE.seo.ogImage,
      noIndex: true,
      canonicalUrl: `${BASE_URL}${canonicalPath}`,
      alternates: [] as Array<{ hrefLang: string; href: string }>,
    };
  }

  const canonicalPath = localizePath(route.path, language);
  const alternates = route.indexable
    ? [
        ...AVAILABLE_LANGUAGES.map((lang) => ({
          hrefLang: lang,
          href: `${BASE_URL}${localizePath(route.path, lang)}`,
        })),
        {
          hrefLang: "x-default",
          href: `${BASE_URL}${route.path}`,
        },
      ]
    : [];

  return {
    language,
    title: resolveLocalizedValue(route.seo.title, language),
    description: resolveLocalizedValue(route.seo.description, language),
    ogTitle: route.seo.ogTitle ? resolveLocalizedValue(route.seo.ogTitle, language) : undefined,
    ogDescription: route.seo.ogDescription ? resolveLocalizedValue(route.seo.ogDescription, language) : undefined,
    ogImage: route.seo.ogImage || "/brand/logo-wordmark-black.png",
    noIndex: route.seo.noIndex || !route.indexable,
    canonicalUrl: `${BASE_URL}${canonicalPath}`,
    alternates,
  };
}

export function getPrerenderPaths() {
  return ROUTE_DEFINITIONS
    .filter((route) => route.indexable)
    .flatMap((route) =>
      AVAILABLE_LANGUAGES.map((language) => localizePath(route.path, language)),
    );
}
